import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'The UTXO Model',
    tagline: 'Bitcoin doesn\'t track balances. It tracks unspent outputs.',
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - what happens when you spend part of a UTXO's value (change output)
//   Q1 - why the UTXO model makes double-spending mechanically difficult
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 3 of 8" title="No account has a balance field">
            <Prose>
                A bank account has a single number that goes up and down. Ethereum's account model works the same
                way. Each account has a balance the network updates directly. Bitcoin works differently: the ledger
                tracks individual <strong>unspent transaction outputs</strong>, or UTXOs: discrete chunks of value,
                each created by some earlier transaction and not yet spent.
            </Prose>
            <FactGrid items={[
                ['Account model (e.g. Ethereum)', 'Each account has one running balance. A transaction directly increases or decreases it.'],
                ['UTXO model (Bitcoin)', 'Value exists as separate, discrete outputs. A wallet\'s "balance" is just the sum of every UTXO it can spend.'],
            ]} />
        </StageSection>
    )),

    content('consuming-utxos', () => (
        <StageSection title="Spending means consuming, not decrementing">
            <Prose>
                A transaction takes one or more existing UTXOs as inputs (fully consuming each one) and creates
                one or more new UTXOs as outputs. There's no such thing as a partial spend of a single UTXO. If you
                have a 1 BTC UTXO and want to pay 0.3 BTC, the transaction consumes the whole 1 BTC input and
                creates two outputs: 0.3 BTC to the recipient, and a 0.7 BTC "change" output back to you.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('why-no-double-spend', () => (
        <StageSection title="Double-spending becomes a mechanical impossibility">
            <Prose>
                Once a UTXO is used as an input in a confirmed transaction, it's gone; it no longer exists as
                "unspent." A later transaction trying to reference that same UTXO again isn't rejected by policy or
                courtesy; it's rejected because the thing it's pointing at simply isn't there anymore. This is a
                direct, structural consequence of the UTXO model, not a separate rule bolted on top of it.
            </Prose>
            <KeyPoints items={[
                <>Wallets track the UTXO set on your behalf so you never have to think in these terms directly.</>,
                <>Understanding UTXOs now makes the next stage (what's actually inside a transaction) much easier to follow.</>,
            ]} />
        </StageSection>
    )),

    question(1),
];
